import {
  createFileRoute,
  getRouteApi,
  Link,
  notFound,
} from '@tanstack/react-router'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { Markdown } from '@/components/Markdown'
import { Badge } from '@/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import ArticleCta from '@/content/ArticleCta'
import { getPublishedGuide } from '@/lib/cms/server'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/docs/$slug')({
  loader: async ({ params }) => {
    const guide = await getPublishedGuide({ data: { slug: params.slug } })
    if (!guide) throw notFound()
    return guide
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? {
          meta: pageMeta({
            title: `${loaderData.title} · Masklabs`,
            description: loaderData.description,
            path: `/docs/${params.slug}`,
          }),
          links: pageLinks(`/docs/${params.slug}`),
        }
      : {},
  notFoundComponent: GuideNotFound,
  component: GuideArticle,
})

// Prev/next come from the /docs layout's nav (one query, one loader) instead
// of a second ordered scan in the guide loader.
const docsRoute = getRouteApi('/docs')

function GuideArticle() {
  const guide = Route.useLoaderData()
  const { ordered } = docsRoute.useLoaderData()
  const index = ordered.findIndex((g) => g.slug === guide.slug)
  const prev = index > 0 ? ordered[index - 1] : undefined
  const next =
    index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : undefined

  return (
    <div className="w-full max-w-3xl">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/docs">Docs</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{guide.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <article>
        {guide.category === 'Advanced' ? (
          <Badge>{guide.category}</Badge>
        ) : (
          <p className="text-sm font-medium text-primary">{guide.category}</p>
        )}
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-balance sm:text-4xl">
          {guide.title}
        </h1>
        <p className="mt-3 text-base text-muted-foreground text-pretty">
          {guide.description}
        </p>

        <div className="prose prose-neutral mt-8 max-w-none dark:prose-invert">
          <Markdown>{guide.bodyMd}</Markdown>
        </div>
      </article>

      <nav
        aria-label="Guide pagination"
        className="mt-12 grid gap-3 border-t pt-6 sm:grid-cols-2"
      >
        {prev ? (
          <Link
            to="/docs/$slug"
            params={{ slug: prev.slug }}
            className="group flex flex-col gap-1 rounded-xl border p-4 transition-colors hover:border-primary/40"
          >
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <ArrowLeft className="size-3.5" />
              Previous
            </span>
            <span className="text-sm font-medium">{prev.title}</span>
          </Link>
        ) : (
          <span aria-hidden />
        )}
        {next ? (
          <Link
            to="/docs/$slug"
            params={{ slug: next.slug }}
            className="group flex flex-col gap-1 rounded-xl border p-4 text-right transition-colors hover:border-primary/40"
          >
            <span className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground">
              Next
              <ArrowRight className="size-3.5" />
            </span>
            <span className="text-sm font-medium">{next.title}</span>
          </Link>
        ) : null}
      </nav>

      <ArticleCta heading="Ready to try it?" className="mt-8" />
    </div>
  )
}

function GuideNotFound() {
  return (
    <div className="flex w-full max-w-3xl flex-col items-start gap-4 py-12">
      <h1 className="text-3xl font-bold tracking-tight">Guide not found</h1>
      <p className="text-muted-foreground">
        That guide doesn&apos;t exist or may have moved.
      </p>
      <Button asChild>
        <Link to="/docs">Back to docs</Link>
      </Button>
    </div>
  )
}
